import { useContext, useState } from 'react'
import { routes } from 'config/routes'
import { useForm } from 'libs/form'
import { useTranslation } from 'libs/i18n'
import { BackButton, Header, Form } from 'shared/components'
import { AuthContext } from 'store/AuthContext'
import { ITag } from 'interfaces/tag.interface'
import { useUserRoles } from 'shared/hooks'
import { StoreSettingsContext } from 'store/StoreSettingsContext'
import { useProductOptions } from 'features/products/hooks/useProductOptions'
import { ProductFormSections } from 'features/products/organisms/ProductForm/ProductFormSections'
import { useAddProductValidationSchema } from './hooks/useAddProductValidationSchema'
import { useSetDefaultQuantity } from './hooks/useSetDefaultQuantity'
import { useSetPublishValue } from './hooks/useSetPublishValue'
import { useAddProductMutation } from './mutations/useAddProductMutation'
import { useSetDefaultBooking } from './hooks/useSetDefaultBooking'
import { useProductFieldGates } from 'features/products/hooks/useProductFieldGates'

const defaultColor = '#FFFFFF'

const ProductAddPage = () => {
	const { auth } = useContext(AuthContext)
	const storeCtx = useContext(StoreSettingsContext)
	const { t } = useTranslation()
	const [productColor, setProductColor] = useState(defaultColor)
	const validationResolver = useAddProductValidationSchema()
	const { isUser } = useUserRoles()

	const form = useForm({
		resolver: validationResolver,
		mode: 'onChange'
	})
	const { register, handleSubmit, setValue, getValues, setError } = form
	const gates = useProductFieldGates({ form })

	const { addProductMutation, isAddProductLoading } = useAddProductMutation(setError)

	const userId = auth?.user?.pk
	const options = useProductOptions({
		bookingsEndpoint: '/bookings/',
		bookingsQuery: `user=${userId}&end_date__gt=${
			new Date().toISOString().split('T')[0]
		}&is_active=true`,
		bookingsEnabled: !!userId
	})

	useSetDefaultBooking({ bookings: options.bookings.items, setValue })
	useSetPublishValue(setValue)
	useSetDefaultQuantity(setValue)

	const handleProductColorChange = (data: { hex: string }) => setProductColor(data.hex)

	const onSubmit = (data: any) => {
		const formattedTags =
			data.tags != null && Array.isArray(data.tags) ? data.tags?.map((tag: ITag) => tag.label) : []
		const image = data.image ?? []

		const formData = new FormData()
		formData.append('name', data.name)
		if (!isUser) {
			formData.append('owner_id', auth?.user?.pk)
		}
		if (storeCtx?.storeSettings?.cost_price_enabled) {
			formData.append('cost_price', data?.cost_price)
		}
		formData.append('price.amount', data.price)
		formData.append('booking_id', data?.booking?.value)
		if (storeCtx?.storeSettings?.quantity_enabled) {
			formData.append('quantity', data.quantity)
		}

		if (gates.supplierActive && data.supplier_barcode) {
			formData.append('supplier_barcode', data.supplier_barcode)
		}
		if (gates.supplierActive && data.supplier_sku) {
			formData.append('supplier_sku', data.supplier_sku)
		}
		if (gates.vatActive && data.vat_rate?.value) {
			formData.append('vat_rate', data.vat_rate?.value)
		}
		formData.append('size', data.size)
		formData.append('category_id', data.category?.value ?? '')
		formData.append('product_condition_id', data.productCondition?.value ?? '')
		formData.append('product_fabric_id', data.productFabricType?.value ?? '')
		formData.append('product_gender_id', data.productGender?.value ?? '')
		formData.append('product_type_id', data.productType?.value ?? '')
		if (storeCtx?.storeSettings?.photos_required_to_publish && !getValues('image')) {
			formData.append('published', 'false')
		} else if (storeCtx?.storeSettings?.users_can_publish_products) {
			formData.append('published', data?.published ? 'true' : 'false')
		} else {
			formData.append(
				'published',
				storeCtx?.storeSettings?.default_product_publish_value ? 'true' : 'false'
			)
		}
		formData.append('color', productColor)

		for (const tag of formattedTags) {
			formData.append('tags', tag)
		}

		if (image?.name) {
			formData.append('image', image, image.name)
		}

		addProductMutation({
			data: formData,
			action: data.action
		})
	}

	return (
		<>
			<Header
				title={t('Add product')}
				eyebrow={t('Products')}
				actions={<BackButton to={routes.productIndex} text={t('Back to products')} />}
			/>
			<div className="px-5 lg:px-0">
				<Form
					variant="sections"
					submitText={t('Add product')}
					secondSubmitText={t('Add and create another')}
					onSubmit={handleSubmit(onSubmit)}
					setValue={setValue}
					isLoading={isAddProductLoading}>
					<input type="hidden" {...register('action')} />
					<ProductFormSections
						form={form}
						options={options}
						gates={gates}
						productColor={productColor}
						onColorChange={handleProductColorChange}
					/>
				</Form>
			</div>
		</>
	)
}

export default ProductAddPage
